import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { PendingDrafts } from '../../app/drafts/PendingDrafts'
import { ApiError } from '../../api/problem'
import {
  useCharacterList,
  useCreateCharacter,
  useSetCharacterHidden,
} from '../../domains/characters/queries'
import type { CharacterSummary } from '../../domains/characters/types'
import { Banner, Button, Card, IconButton, Input, SectionLabel } from '../../design/components'
import {
  CHARACTER_TYPE_ORDER,
  CLASS_TYPE_ORDER,
  SKILL_POOL_ORDER,
  characterTypeLabel,
  classTypeLabel,
  skillPoolLabel,
} from '../../domains/characters/labels'
import { useAbilityReference } from '../../domains/reference/queries'
import { useDebounced } from '../../lib/useDebounced'
import type { components } from '../../api/generated/schema'
import styles from './characters.module.css'

type CharacterType = components['schemas']['CharacterType']
type CharacterClassType = components['schemas']['CharacterClassType']
type SkillPoolOption = components['schemas']['SkillPoolOption']

export function CharacterListPage() {
  const [creating, setCreating] = useState(false)
  const [filter, setFilter] = useState('')

  /*
   * Hidden characters are off by default and the toggle is per visit, not remembered: this
   * screen is the one place they can be got back, so it has to be obvious that they are being
   * left out — and a preference that persisted would eventually leave someone looking at a
   * roster they thought was filtered.
   */
  const [showHidden, setShowHidden] = useState(false)

  // The API filters before paging, so searching narrows the whole roster rather
  // than just the loaded page. Debounced so a search is one request per pause,
  // not one per keystroke.
  const search = useDebounced(filter)
  const query = useCharacterList(1, 50, search, true, showHidden)

  const visible = query.data?.items ?? []
  const filtering = search.trim() !== ''

  return (
    <AppShell title="Characters" subtitle="YOUR ROSTER">
      {/*
        Above the roster, because a proposed character is not on the roster yet — this is where
        somebody would look for one and not find it.
      */}
      <PendingDrafts />

      {query.isPending && <Card>Loading…</Card>}

      {query.isError && (
        <Card>
          <Banner tone="error" urgent>
            Could not load your characters.
          </Banner>
        </Card>
      )}

      {/* The roster's own header, pinned under the app's. A long roster used to hide the
          only way to add to it at the very bottom of the scroll; here it is the first thing
          on the screen and it stays there. The icon carries it — the label beside it already
          says what the section is, so a word on the button would say it twice. */}
      {query.isSuccess && (
        <div className={styles.listHead}>
          <SectionLabel tone="ember">Roster</SectionLabel>
          <span className={styles.listHeadEnd}>
            {/* totalCount describes the filtered set — the API searches before it pages —
                so while filtering this counts matches, not the roster. */}
            <span className={styles.listCount}>{countLabel(query.data.totalCount, filtering)}</span>
            {/* The eye is the same glyph the character menu hides with, so the two read as one
                idea: crossed out means "hidden characters are being left out". */}
            <IconButton
              label={showHidden ? 'Leave out hidden characters' : 'Include hidden characters'}
              icon={showHidden ? 'show' : 'hide'}
              aria-pressed={showHidden}
              className={showHidden ? styles.toggleOn : undefined}
              onClick={() => setShowHidden((value) => !value)}
            />
            <IconButton
              label="New character"
              icon="add"
              disabled={creating}
              onClick={() => setCreating(true)}
            />
          </span>
        </div>
      )}

      {creating && <CreateCharacterCard onDone={() => setCreating(false)} />}

      {query.isSuccess && visible.length === 0 && !filtering && !creating && (
        <Card label="No characters yet">
          <p className={styles.note}>Create one to get started.</p>
        </Card>
      )}

      {(query.isSuccess || filtering) && (visible.length > 0 || filtering) && (
        <Input
          label="Filter by name"
          type="search"
          placeholder="Start typing…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}

      {query.isSuccess && filtering && visible.length === 0 && (
        <Card>
          <p className={styles.note}>No character matches &ldquo;{search.trim()}&rdquo;.</p>
        </Card>
      )}

      {query.isSuccess && visible.length > 0 && (
        <ul className={styles.list}>
          {visible.map((character) => (
            <RosterRow key={character.id} character={character} />
          ))}
        </ul>
      )}

      {query.isSuccess && query.data.totalCount > visible.length && (
        <p className={styles.note}>
          {/* totalCount describes the filtered set, because the API applies the
              search before paging — so this counts matches, not the whole roster. */}
          Showing {visible.length} of {query.data.totalCount}
          {filtering ? ' matches' : ''}.
        </p>
      )}
    </AppShell>
  )
}

/**
 * One roster row: the character, and the control that puts it away.
 *
 * A component rather than markup inside the map, because each row runs its own mutation and a
 * hook cannot be called in a loop.
 *
 * The card is the `<li>` and the link sits inside it, rather than the link being the card — a
 * button nested in an anchor is invalid, and more to the point a click meant for "hide" would
 * navigate instead. This is the arrangement that lets the two live in one row.
 */
function RosterRow({ character }: { character: CharacterSummary }) {
  const isHidden = character.isHidden === true
  const setHidden = useSetCharacterHidden(character.id ?? 0)

  return (
    <li className={isHidden ? `${styles.row} ${styles.rowHidden}` : styles.row}>
      <Link to={`/characters/${character.id}`} className={styles.rowLink}>
        <Portrait url={character.portraitUrl ?? null} name={character.name ?? ''} />
        <span className={styles.rowText}>
          <span className={styles.rowName}>{character.name}</span>
          <span className={styles.rowMeta}>
            {[
              character.type,
              character.classType,
              `Refresh ${character.totalRefresh ?? 0}`,
              // Only ever present while the toggle is on, and worth saying in words:
              // a dimmed row alone does not tell you why it is dimmed.
              isHidden ? 'Hidden' : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      </Link>

      {/* Hiding from here makes the row leave the list, which is the whole point — but it is
          also the one action on this screen with no visible result to check. So the name is
          spelled out per row: a screen reader hears which character is being put away, and a
          hover tooltip says it before the click rather than after. */}
      <IconButton
        className={styles.rowAction}
        label={
          isHidden ? `Show ${character.name} in my lists` : `Hide ${character.name} from my lists`
        }
        icon={isHidden ? 'show' : 'hide'}
        disabled={setHidden.isPending}
        onClick={() => setHidden.mutate(!isHidden)}
      />
    </li>
  )
}

/** e.g. "12 CHARACTERS", or "3 MATCHES" while a filter is narrowing the roster. */
function countLabel(total: number, filtering: boolean): string {
  const noun = filtering ? 'match' : 'character'
  return `${total} ${total === 1 ? noun : filtering ? 'matches' : 'characters'}`
}

function Portrait({ url, name }: { url: string | null; name: string }) {
  if (url === null || url === '') {
    return (
      <span className={styles.avatarFallback} aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </span>
    )
  }
  return <img className={styles.avatar} src={url} alt="" loading="lazy" />
}

/**
 * Character creation, matching the Blazor page's fields: name, type, class,
 * power level, and — for a shapeshifter — its shapes.
 *
 * The power level is the load-bearing one. It sets the skill-point budget, and
 * the server derives starting refresh from it; a character created without one
 * has no pool at all, which leaves its budget reading 0 total and a negative
 * remainder as soon as a skill is taken.
 */
function CreateCharacterCard({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate()
  const create = useCreateCharacter()

  const [name, setName] = useState('')
  const [type, setType] = useState<CharacterType>('PC')
  const [classType, setClassType] = useState<CharacterClassType>('Mortal')
  const [skillPool, setSkillPool] = useState<SkillPoolOption>('FeetInTheWater')
  const [shapes, setShapes] = useState<string[]>(['Human', ''])
  const [vampirismAbilityId, setVampirismAbilityId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isShapeshifter = classType === 'Shapeshifter'
  const namedShapes = shapes.map((s) => s.trim()).filter((s) => s !== '')
  // The Blazor page's rule: a shapeshifter with one shape has nothing to shift to.
  const shapesOk = !isShapeshifter || namedShapes.length >= 2

  // Which kinds of vampire exist is server data, not a constant here: the flag is what
  // makes an ability one, so a table that adds a court gets it in this list for free.
  const isVampire = classType === 'Vampire'
  const abilityReference = useAbilityReference()
  const vampirismAbilities = (abilityReference.data ?? []).filter(
    (ability) => ability.isVampirism === true,
  )
  const vampirismOk = !isVampire || vampirismAbilityId !== null

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      const sheet = await create.mutateAsync({
        name,
        type,
        classType,
        skillPool,
        ...(isShapeshifter ? { shapes: namedShapes } : {}),
        ...(isVampire && vampirismAbilityId !== null ? { vampirismAbilityId } : {}),
      })
      void navigate(`/characters/${sheet.characterId}`)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not create the character.')
    }
  }

  return (
    <Card variant="fate" label="New character">
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        <Input
          label="Name"
          name="name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <Choice
          label="Character type"
          value={type}
          options={CHARACTER_TYPE_ORDER}
          format={characterTypeLabel}
          onChange={setType}
        />

        <Choice
          label="Character class"
          value={classType}
          options={CLASS_TYPE_ORDER}
          format={classTypeLabel}
          onChange={setClassType}
        />

        <Choice
          label="Power level"
          value={skillPool}
          options={SKILL_POOL_ORDER}
          format={skillPoolLabel}
          onChange={setSkillPool}
          hint="Sets the skill-point budget; the server derives starting refresh from it."
        />

        {isVampire && (
          <fieldset className={styles.shapesFieldset}>
            <legend>
              <SectionLabel tone="muted" micro>
                Kind of vampire
              </SectionLabel>
            </legend>

            {abilityReference.isPending ? (
              <p className={styles.note}>Loading…</p>
            ) : vampirismAbilities.length === 0 ? (
              <p className={styles.note}>
                No vampirism abilities are in the library, so a vampire cannot be created.
              </p>
            ) : (
              vampirismAbilities.map((ability) => (
                <label key={ability.id} className={styles.choiceOption}>
                  <input
                    type="radio"
                    name="vampirismAbility"
                    checked={vampirismAbilityId === ability.id}
                    onChange={() => setVampirismAbilityId(ability.id ?? null)}
                  />
                  <span>
                    {ability.name} — {ability.description}
                  </span>
                </label>
              ))
            )}

            {!vampirismOk && vampirismAbilities.length > 0 && (
              <p className={styles.note}>Pick what kind of vampire this is.</p>
            )}
          </fieldset>
        )}

        {isShapeshifter && (
          <fieldset className={styles.shapesFieldset}>
            <legend>
              <SectionLabel tone="muted" micro>
                Shapes
              </SectionLabel>
            </legend>

            {shapes.map((shape, index) => (
              <Input
                key={index}
                label={`Shape ${index + 1}`}
                value={shape}
                placeholder={index === 1 ? 'Wolf' : ''}
                onChange={(e) =>
                  setShapes((prev) => prev.map((s, i) => (i === index ? e.target.value : s)))
                }
              />
            ))}

            <div className={styles.formActions}>
              <Button variant="secondary" onClick={() => setShapes((prev) => [...prev, ''])}>
                Add another shape
              </Button>
            </div>

            {!shapesOk && (
              <p className={styles.note}>A shapeshifter needs at least two named shapes.</p>
            )}
          </fieldset>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={create.isPending || name.trim() === '' || !shapesOk || !vampirismOk}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

/** A labelled radio group — options are short and the choice matters, so no select. */
function Choice<T extends string>({
  label,
  value,
  options,
  format,
  onChange,
  hint,
}: {
  label: string
  value: T
  options: readonly T[]
  format: (value: T) => string
  onChange: (next: T) => void
  hint?: string
}) {
  return (
    <fieldset className={styles.choice}>
      <legend>
        <SectionLabel tone="muted" micro>
          {label}
        </SectionLabel>
      </legend>

      {options.map((option) => (
        <label key={option} className={styles.choiceOption}>
          <input
            type="radio"
            name={label}
            value={option}
            checked={value === option}
            onChange={() => onChange(option)}
          />
          <span>{format(option)}</span>
        </label>
      ))}

      {hint !== undefined && <p className={styles.note}>{hint}</p>}
    </fieldset>
  )
}
